import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Button, Stack, Tooltip } from '@mui/material';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import MutationKeys from 'hooks/mutations/keys';
import useMutationStatus from 'hooks/useMutationStatus';
import { MutationStatus } from 'models/enums';
import { ContentType, LandingPage, Template } from 'domains/content/types';
import PageLayout from 'domains/core/components/PageLayout';
import ContentIcon from 'domains/core/components/SvgIcons/ContentIcon';
import DropdownButton from 'domains/core/components/DropdownButton';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import ContentTable from '../ContentTable';
import { Props as ContentTableProps } from '../ContentTable/ContentTable';
import { DropdownButtonOption } from 'domains/core/components/DropdownButton/DropdownButton';
import { Folder } from 'models/types';
import SearchField from 'domains/core/components/SearchField';
import MultiSelectDropdown, { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';
import { ContentStatus } from '../ContentTable/ContentStatusText';
import { useFlags } from 'launchdarkly-react-client-sdk';

const contentStatusOptions: MultiSelectOption<ContentStatus>[] = [
    { value: ContentStatus.DRAFT, label: 'Draft' },
    { value: ContentStatus.PUBLISHED, label: 'Published' },
    { value: ContentStatus.LIVE, label: 'Live' },
];

export const contentTypeToLabel = (type: ContentType | null): string => {
    if (!type) return ContentType.ALL;
    if (type === ContentType.DIRECT_MAIL) return 'Direct Mail';
    if (type === ContentType.PAGE) return 'Landing Page';
    if (type === ContentType.SMS) return 'Text Message';
    if (type === ContentType.VOICE) return 'Voice Call';

    return type;
};

export type Props = {
    contentQuery: ContentTableProps<Template | LandingPage | Folder>['contentQuery'];
    createContentDropdownOptions: DropdownButtonOption[];
    headerOtherDropdownProps?: {
        label: string;
        options: DropdownButtonOption[];
    };
    isNestedContent?: boolean;
    isArchivedView?: boolean;
    onToggleArchivedView?: () => void;
    searchTerm?: string;
    onSearchTermChange?: (value: string) => void;
    selectedStatuses?: ContentStatus[];
    onStatusFilterApply?: (params: { newStatuses?: ContentStatus[] }) => void;
};

const ContentPageLayout = ({
    contentQuery,
    createContentDropdownOptions,
    headerOtherDropdownProps,
    isNestedContent,
    isArchivedView = false,
    onToggleArchivedView,
    searchTerm,
    onSearchTermChange,
    selectedStatuses = [],
    onStatusFilterApply,
}: Props) => {
    const { contentDraft: hasContentDraft } = useFlags();
    const createLandingPageStatus = useMutationStatus(MutationKeys.CREATE_LANDING_PAGE);
    const createTemplateStatus = useMutationStatus(MutationKeys.CREATE_TEMPLATE);
    const updateLandingPageStatus = useMutationStatus(MutationKeys.UPDATE_LANDING_PAGE);
    const updateTemplateStatus = useMutationStatus(MutationKeys.UPDATE_TEMPLATE);
    const createFolderStatus = useMutationStatus(MutationKeys.CREATE_CONTENT_FOLDER);
    const createVoiceContentStatus = useMutationStatus(MutationKeys.CREATE_VOICE_CONTENT);
    const updateVoiceContentStatus = useMutationStatus(MutationKeys.UPDATE_VOICE_CONTENT);
    const createSmsStatus = useMutationStatus(MutationKeys.CREATE_SMS);
    const updateSmsStatus = useMutationStatus(MutationKeys.UPDATE_SMS);
    const createWhatsAppStatus = useMutationStatus(MutationKeys.CREATE_WHATSAPP_TEMPLATE);
    const updateWhatsAppStatus = useMutationStatus(MutationKeys.UPDATE_WHATSAPP_TEMPLATE);

    const { data, isError, isSuccess } = contentQuery;
    const isEmpty = isSuccess && !data?.length;
    const shouldShowCreateButton = (!isEmpty || isNestedContent || searchTerm) && !isArchivedView;

    const CreateButton = (
        <DropdownButton
            aria-label="Choose content type to create"
            data-testid="content-create-button"
            label="Create"
            variant="outlined"
            options={createContentDropdownOptions}
            icon={<ContentIcon />}
        />
    );

    const handleStatusFilterApply = (statuses: ContentStatus[]) => {
        onStatusFilterApply?.({ newStatuses: statuses });
    };

    const renderHeaderOthers = () => {
        if (!headerOtherDropdownProps && !onStatusFilterApply) return;

        return (
            <Stack direction="row" spacing={0} alignItems="center">
                {headerOtherDropdownProps && (
                    <DropdownButton
                        sx={{ textTransform: 'capitalize' }}
                        aria-label="Choose which type of content to view"
                        variant="text"
                        icon={<KeyboardArrowDownIcon />}
                        {...headerOtherDropdownProps}
                    />
                )}

                {hasContentDraft && onStatusFilterApply && (
                    <MultiSelectDropdown
                        options={contentStatusOptions}
                        selectedValues={selectedStatuses}
                        onApply={handleStatusFilterApply}
                        label="Status"
                        testIdPrefix="status-filter"
                        menuId="status-filter-menu"
                    />
                )}

                {onToggleArchivedView && (
                    <Tooltip
                        key={`content-archive-tooltip-${isArchivedView}`}
                        title={isArchivedView ? 'Original content' : 'Archive list'}
                    >
                        <Button
                            onClick={onToggleArchivedView}
                            data-testid="archive-toggle-button"
                            sx={{ ml: 0, mr: 0.5 }}
                        >
                            {isArchivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
                        </Button>
                    </Tooltip>
                )}
                {onSearchTermChange && (
                    <SearchField
                        placeholder="Search content..."
                        value={searchTerm || ''}
                        onChange={onSearchTermChange}
                        dataTestId="content-search-input"
                    />
                )}
            </Stack>
        );
    };

    const renderPageBody = () => {
        if (isError) {
            return (
                <EmptyStateLayout
                    caption="Please try refreshing the page."
                    header="There was a problem loading the content."
                />
            );
        }

        // Show empty table if search term exists, even when empty
        const hasSearchTerm = searchTerm && searchTerm.trim().length > 0;
        const shouldShowEmptyTable = isEmpty && (isNestedContent || hasSearchTerm);

        if (isEmpty && !isNestedContent && !hasSearchTerm) {
            return (
                <EmptyStateLayout
                    caption={
                        isArchivedView
                            ? "You don't have any archived content."
                            : 'You currently do not have any content saved.'
                    }
                    header={isArchivedView ? 'No archived content' : "let's design your content"}
                >
                    {!isArchivedView && CreateButton}
                </EmptyStateLayout>
            );
        }

        return (
            <ContentTable
                contentQuery={contentQuery}
                shouldShowEmptyTable={shouldShowEmptyTable}
                searchTerm={searchTerm}
            />
        );
    };

    return (
        <PageLayout
            header={isArchivedView ? 'archived content' : 'content'}
            headerIcon={<ContentIcon fontSize="inherit" />}
            {...(shouldShowCreateButton && { headerAction: CreateButton })}
            headerOthers={renderHeaderOthers()}
        >
            {createTemplateStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Email successfully saved." />
            )}
            {updateTemplateStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Email successfully updated." />
            )}
            {createLandingPageStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Landing page successfully saved." />
            )}
            {updateLandingPageStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Landing page successfully updated." />
            )}
            {createFolderStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Folder created successfully." />
            )}
            {createVoiceContentStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Voice content created successfully." />
            )}
            {updateVoiceContentStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Voice content successfully updated." />
            )}
            {createSmsStatus === MutationStatus.SUCCESS && <SuccessSnackbar successMessage="SMS successfully saved." />}
            {updateSmsStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="SMS successfully updated." />
            )}
            {createWhatsAppStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="WhatsApp template successfully saved." />
            )}
            {updateWhatsAppStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="WhatsApp template successfully updated." />
            )}
            {renderPageBody()}
        </PageLayout>
    );
};

export default ContentPageLayout;
